// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Opt-in shot detail: fairway, putts and green in regulation, entered on your own score in the
 * hole score popup. These ride the ordinary hole-event pipeline as the event types `fairway`,
 * `putts` and `gir`, which no game calculator resolves, so they never affect scoring.
 */

/** Which side of the fairway a tee shot finished on. Par 3s never ask. */
export type FairwayResult = 'hit' | 'left' | 'right';

/** The shot detail for one player on one hole. Null means "not answered". */
export type EnhancedHoleValues = {
  fairway: FairwayResult | null;
  putts: number | null;
  /**
   * Never entered by hand: the server works it out from the score, putts and par. Rounds scored
   * before that read an explicit answer here, which still outranks the local derivation but not
   * the server's own answer; see deriveGreenInRegulation.
   */
  gir: boolean | null;
};

export const EMPTY_ENHANCED_VALUES: EnhancedHoleValues = { fairway: null, putts: null, gir: null };

export const ENHANCED_EVENT_TYPES = ['fairway', 'putts', 'gir'] as const;

export const MAX_TRACKED_PUTTS = 6;

/** Left to right, the order the three fairway buttons sit in. */
export const FAIRWAY_RESULTS: readonly FairwayResult[] = ['left', 'hit', 'right'];

export const parseFairwayResult = (value: string | null | undefined): FairwayResult | null => {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'hit' || normalized === 'left' || normalized === 'right') {
    return normalized;
  }
  return null;
};

/** Stepping below zero clears the field, so a mis-tap does not leave a 0 on an unplayed hole. */
export const stepPutts = (current: number | null, direction: 1 | -1): number | null => {
  if (current === null) {
    return direction === 1 ? 1 : null;
  }
  const next = current + direction;
  if (next < 0) return null;
  return Math.min(next, MAX_TRACKED_PUTTS);
};

/**
 * A green is hit in regulation when the ball is on the putting surface with at least two strokes
 * left for par, so strokes minus putts has to come in at par - 2 or better. Returns null while
 * either half is still missing rather than guessing.
 *
 * Used only as a display fallback until the server-provided value arrives; the server owns the
 * answer and ships it on the hole entry. Mirrors IsGreenInRegulation in
 * services/shared/Goffy.Scoring/GreenInRegulation.cs, and ShotDetail.deriveGir on the watch.
 */
export const deriveGreenInRegulation = (
  grossStrokes: number | null,
  putts: number | null,
  par: number | null
): boolean | null => {
  if (grossStrokes === null || putts === null || par === null) return null;
  return grossStrokes - putts <= par - 2;
};
